import * as React from 'react';
import { TranslateFn } from '@wix/answers-app-core';
import { BaseWidgetProps } from '../../widget-container';
import { DayData, DailyActivity, BusinessHours, WidgetChatSettings } from '@wix/answers-api';
import { classNames } from '@wix/answers-lib';
import { sanitizeExceptWidgetSdk } from '@wix/answers-widget-viewer';

export const chatClosedKey = 'chat-closed';

export type ChatClosedProps = {
	baseWidgetProps: BaseWidgetProps;
};

export const days: Array<keyof DayData> = [
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
	'friday',
	'saturday',
	'sunday'
];

export type GroupedDailyHours = {
	days: Array<keyof DayData>;
	hours: DailyActivity;
};

const areHoursEqual = (day1: DailyActivity, day2: DailyActivity) => {
	if (!day1.enabled && !day2.enabled) {
		return true;
	}
	return day1.start[0] === day2.start[0] && day1.start[1] === day2.start[1]
		&& day1.end[0] === day2.end[0] && day1.end[1] === day2.end[1];
};

const getGroupedBusinessHours = (businessHours: BusinessHours) => {
	const hoursArray = days.map((day) => ({day, hours: businessHours[day]}));
	const groupedArray: GroupedDailyHours[] = [];
	hoursArray.forEach((day) => {
		const lastDay = groupedArray[groupedArray.length - 1];
		if (lastDay && areHoursEqual(lastDay.hours, day.hours)) {
			groupedArray[groupedArray.length - 1].days.push(day.day);
		} else {
			groupedArray.push({days: [day.day], hours: day.hours});
		}
	});
	return groupedArray;
};

export const getPrettyHour = (hour: number[], locale: string) => {
	const time = new Date();
	time.setHours(hour[0]);
	time.setMinutes(hour[1]);
	const timeString = time.toLocaleTimeString(locale, {hour: 'numeric', minute: '2-digit'});
	const lowerCaseTimeString = timeString.replace(' AM', 'am').replace(' PM', 'pm');
	return lowerCaseTimeString;
};

const translationPrefix = `widget-ng.chat.${chatClosedKey}`;

export type ChatClosedState = {
	chatModuleSettings: WidgetChatSettings
};

export class ChatClosed extends React.PureComponent<ChatClosedProps, ChatClosedState> {

	state: ChatClosedState = {
		chatModuleSettings: this.props.baseWidgetProps.widgetConfig.chatModuleSettings
	};

	tp: TranslateFn = (key, params) => {
		return this.props.baseWidgetProps.translate(`${translationPrefix}.${key}`, params);
	}

	componentDidMount () {
		if (window) {
			window.addEventListener('message', this.handleSettingsMessage);
		}
	}

	componentWillUnmount () {
		if (window) {
			window.removeEventListener('message', this.handleSettingsMessage);
		}
	}

	handleSettingsMessage = (event: MessageEvent) => {
		const newSettings: any = event.data.previewNewSettings;
		if (newSettings) {
			const settings = newSettings.chatModule || this.state.chatModuleSettings;
			this.setState({ chatModuleSettings: settings });
		}
	}

	renderHoursItem = (hours: DailyActivity, key: number) => {
		const locale = this.props.baseWidgetProps.locale;
		if (!hours.enabled) {
			return <div className='hours-item' key={key} data-hook={'closed'}>{this.tp('closed')}</div>;
		}
		const startTime = getPrettyHour(hours.start, locale);
		const endTime = getPrettyHour(hours.end, locale);
		return (
			<div className='hours-item' key={key} data-hook={`${startTime} - ${endTime}`}>
				{this.tp('time-range', {startTime, endTime})}
			</div>
		);
	}

	renderHoursColumn = (groupedBusinessHours: GroupedDailyHours[]) => {
		return (
			<div className='hours-column'>
				{groupedBusinessHours.map((group, i) => this.renderHoursItem(group.hours, i))}
			</div>
		);
	}

	renderDayGroup = (daysGroup: string[], key: number) => {
		if (daysGroup.length === 1) {
			return <div className='day' key={key} data-hook={daysGroup[0]}>{this.tp(`day.${daysGroup[0]}`)}</div>;
		}
		const firstDay = daysGroup[0];
		const lastDay = daysGroup[daysGroup.length - 1];
		return (
			<div className='day' key={key} data-hook={`${firstDay} - ${lastDay}`}>
				{`${this.tp(`day.${firstDay}`)} - ${this.tp(`day.${lastDay}`)}`}
			</div>
		);
	}

	renderDaysColumn = (groupedBusinessHours: GroupedDailyHours[]) => {
		return (
			<div className='days-column'>
				{groupedBusinessHours.map((group, i) => this.renderDayGroup(group.days, i))}
			</div>
		);
	}

	renderBusinessHours = () => {
		const businessHours = this.state.chatModuleSettings.businessHours;
		const groupedBusinessHours = getGroupedBusinessHours(businessHours);
		return (
			<div className='business-hours'>
				<div className='business-hours-title'>{this.tp('title')}</div>
				<div className='opening-hours'>
					{this.renderDaysColumn(groupedBusinessHours)}
					{this.renderHoursColumn(groupedBusinessHours)}
				</div>
			</div>
		);
	}

	render () {
		const {appearance} = this.props.baseWidgetProps.widgetConfig;
		const backgroundColor = appearance.brandColor;
		const {blockContact} = this.state.chatModuleSettings.outsideBusinessHoursSettings;
		const {addMessage, showBusinessHours} = blockContact;
		const title = blockContact.title || this.tp('defaults.title');
		const subtitle = blockContact.subtitle || this.tp('defaults.subtitle');
		const message = sanitizeExceptWidgetSdk(blockContact.message || this.tp('defaults.message'));

		return (
			<div className={classNames(chatClosedKey, {'no-business-hours': !showBusinessHours})}>
				<div className='title-container' style={{background: backgroundColor}}>
					<div className='title'>{title}</div>
					<div className='subtitle'>{subtitle}</div>
				</div>
				{showBusinessHours ? this.renderBusinessHours() : null}
				{addMessage ? <div className='closing-message' dangerouslySetInnerHTML={{__html: message}} /> : null}
			</div>
		);
	}
}
